<template>
  <div class="w-full bg-indigo-100 border-b border-indigo-200 shadow-sm py-2">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Versión desktop -->
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <!-- Texto principal -->
          <span class="text-indigo-700 font-bold">¡Ahorra tiempo con nuestra extensión! </span> <span class="text-indigo-700 font-normal">Registra tu anuncio en segundos o muestra interés antes que nadie.</span>
        </div>
        
        <!-- Botones de acción -->
        <div class="flex items-center gap-3">
          <a :href="extensionUrl" target="_blank" class="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium rounded-full hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            <!-- Icono de puzzle dentro del botón -->
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5 mr-2 text-white">
              <path d="M11.25 5.337c0-.355-.186-.676-.401-.959a1.647 1.647 0 0 1-.349-1.003c0-1.036 1.007-1.875 2.25-1.875S15 2.34 15 3.375c0 .369-.128.713-.349 1.003-.215.283-.401.604-.401.959 0 .332.278.598.61.578 1.91-.114 3.79-.342 5.632-.676a.75.75 0 0 1 .878.645 49.17 49.17 0 0 1 .376 5.452.657.657 0 0 1-.66.664c-.354 0-.675-.186-.958-.401a1.647 1.647 0 0 0-1.003-.349c-1.035 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401.31 0 .557.262.534.571a48.774 48.774 0 0 1-.595 4.845.75.75 0 0 1-.61.61c-1.82.317-3.673.533-5.555.642a.58.58 0 0 1-.611-.581c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.035-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959a.641.641 0 0 1-.658.643 49.118 49.118 0 0 1-4.708-.36.75.75 0 0 1-.645-.878c.293-1.614.504-3.257.629-4.924A.53.53 0 0 0 5.337 15c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.036 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.369 0 .713.128 1.003.349.283.215.604.401.959.401a.656.656 0 0 0 .659-.663 47.703 47.703 0 0 0-.31-4.82.75.75 0 0 1 .83-.832c1.343.155 2.703.254 4.077.294a.64.64 0 0 0 .657-.642Z" />
            </svg>
            Añadir
          </a>
          <button @click="toggleInfo" class="text-indigo-700 text-sm hover:text-indigo-900 transition-colors duration-200 border border-transparent hover:border-indigo-300 px-3 py-2 rounded-lg">
            {{ showInfo ? 'Ocultar info' : 'Cómo funciona' }}
          </button>
          
          <!-- Botón para cerrar el banner -->
          <button @click="closeBanner" class="ml-2 text-gray-400 hover:text-gray-600 transition-colors duration-200">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
      
      <!-- Panel de información desplegable -->
      <transition name="fade">
        <div v-if="showInfo" class="mt-3">
          <div class="text-xs text-gray-700 bg-indigo-50 p-4 rounded-lg border border-indigo-100 shadow-sm mb-2">
            <p class="mb-3">
               Navega entre propiedades en portales como Idealista, Fotocasa (próximamente) o similares. Con un clic en la extensión, podrás mostrar tu interés o registrar automáticamente todos los datos en Notarial.
              </p>
              <!-- En escritorio se muestra horizontal, en móvil vertical -->
              <div class="flex flex-col md:flex-row items-start md:items-center md:space-x-4 space-y-2 md:space-y-0 bg-white p-2 rounded-md shadow-inner border border-indigo-50">
                <!-- Punto 1 -->
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium">Ahorra hasta el 90% del tiempo.</span>
                </div>
                
                <!-- Punto 2 -->
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium">Consigue más visibilidad.</span>
                </div>
                
                <!-- Punto 3 -->
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium">Evita las comisiones inmobiliarias.</span>
                </div>
              </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  isForOwner: {
    type: Boolean,
    default: true
  },
  initialShowInfo: {
    type: Boolean,
    default: false
  },
  extensionUrl: {
    type: String,
    default: 'https://chromewebstore.google.com/detail/laikglahgcnjmohmigpmdfclaphjcihm?utm_source=item-share-cb'
  },
  customClass: {
    type: String,
    default: ''
  }
});

const showInfo = ref(props.initialShowInfo);

const emit = defineEmits(['info-toggled', 'close']);

const toggleInfo = () => {
  showInfo.value = !showInfo.value;
  emit('info-toggled', showInfo.value);
};

const closeBanner = () => {
  emit('close');
};

// Actualizar el estado local cuando cambia la prop
watch(() => props.initialShowInfo, (newValue) => {
  showInfo.value = newValue;
});
</script>

<style scoped>
/* Transition effects for extension info panel */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style> 